import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Share2,
  ArrowRight,
  Building2,
  Ruler,
  Sparkles,
  PhoneCall,
  CheckCircle2,
  Send,
  Home,
} from "lucide-react";
import { motion } from "framer-motion";

const sampleSoldProperties = [
  {
    id: "premium-french-rowhouses",
    title: "Premium French-Inspired Rowhouses",
    location: "Soukya Road, Whitefield, Bengaluru",
    status: "EOI Active",
    type: "Rowhouse",
    area: "20 Acres",
    units: "242 Rowhouses",
    price: "₹5.40 Cr onwards",
    highlight: "Featured Showcase",
    image:
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=85",
    url: "/premium-french-rowhouses",
    description:
      "Showcased with 4K drone cinematography and direct marketing to 15,000+ pre-approved high-net-worth buyers.",
  },
  {
    id: "brigade-granada",
    title: "Brigade Granada",
    location: "Seegahalli Road, East Bengaluru",
    status: "EOI Active",
    type: "Township",
    area: "40+ Acres",
    units: "55-Floor Towers",
    price: "₹1.75 Cr onwards",
    highlight: "Top Performing",
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=85",
    url: "/brigade-granada",
    description:
      "Listed with digital social campaigns generating over 200 qualified site visit enquiries within the first 14 days.",
  },
  {
    id: "sanjeevini-heliconia",
    title: "Sanjeevini Heliconia",
    location: "Soukya Road, Whitefield, Bengaluru",
    status: "RERA Registered",
    type: "Residential",
    area: "1,175 – 1,690 ft²",
    units: "Exclusive Residences",
    price: "₹85 Lakhs onwards",
    highlight: "Fast Closing",
    image:
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200&q=85",
    url: "/sanjeevini-heliconia",
    description:
      "Verified documentation package prepared upfront, ensuring lightning-fast bank loan approvals for prospective buyers.",
  },
];

const Sell = () => {
  const [formData, setFormData] = useState({
    ownerName: "",
    phone: "",
    propertyType: "Apartment",
    projectName: "",
    location: "",
    expectedPrice: "",
    bhk: "3 BHK",
    area: "",
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const message = `📋 *NEW PROPERTY SELL / LISTING ENQUIRY*

👤 Owner Name: ${formData.ownerName || "-"}
📞 Phone: ${formData.phone || "-"}
🏡 Property Type: ${formData.propertyType}
🏢 Project / Building: ${formData.projectName || "-"}
📍 Location: ${formData.location || "-"}
💰 Expected Price: ${formData.expectedPrice || "-"}
🛏 Configuration: ${formData.bhk}
📐 Super Built-Up Area: ${formData.area || "-"}

Hi HousyHunt, I would like to list and sell my property with you. Please get in touch for valuation and next steps.`;

    const whatsappUrl = `https://wa.me/918088687429?text=${encodeURIComponent(
      message
    )}`;

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    setSubmitted(true);
  };

  const handleShare = (e, property) => {
    e.preventDefault();
    e.stopPropagation();

    const message = `🏡 *${property.title}*

📍 ${property.location}
💰 Price: ${property.price}
✨ Type: ${property.type}
📐 Area: ${property.area}

${property.description}

🔗 View Listing:
${window.location.origin}${property.url}

Hi HousyHunt, I want to sell a similar property in this area. Please connect with me.`;

    const whatsappUrl = `https://wa.me/918088687429?text=${encodeURIComponent(
      message
    )}`;

    window.open(whatsappUrl, "_blank");
  };

  return (
    <div className="w-full bg-[#FAFCFF] min-h-screen">
      {/* ================= HERO & SELL FORM ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-red-50/80 via-white to-[#FAFCFF] pt-12 pb-20 px-4 sm:px-6 lg:px-14 border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* LEFT: PITCH & STATS */}
            <div className="lg:col-span-6">
              <span className="inline-flex items-center gap-2 bg-red-100/70 text-red-600 border border-red-200/80 px-4 py-2 rounded-full text-xs sm:text-sm font-bold tracking-wide uppercase mb-5 shadow-sm">
                <Sparkles size={15} />
                Sell With HousyHunt
              </span>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 tracking-tight leading-[1.15]">
                Sell Your Property,{" "}
                <span className="text-red-600">Faster & at Top Value.</span>
              </h1>

              <p className="mt-5 text-gray-600 text-base sm:text-lg leading-relaxed">
                Connect with 50,000+ verified buyers across Bengaluru. Get free
                property valuation, professional HD photoshoots, and complete
                legal closing support.
              </p>

              {/* STATS STRIP */}
              <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-gray-200/70">
                <div>
                  <p className="text-3xl font-black text-red-600">98.9%</p>
                  <p className="text-xs text-gray-500 font-medium mt-1">
                    Successful Closures
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-black text-gray-900">15 Days</p>
                  <p className="text-xs text-gray-500 font-medium mt-1">
                    Avg. First Offer
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-black text-gray-900">₹0</p>
                  <p className="text-xs text-gray-500 font-medium mt-1">
                    Upfront Listing Fee
                  </p>
                </div>
              </div>

              {/* TRUST BULLETS */}
              <div className="mt-8 space-y-3">
                {[
                  "Free high-definition photography and video showcase",
                  "Direct connection with qualified, loan-pre-approved buyers",
                  "End-to-end documentation, draft sale agreement & registry assistance",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-sm text-gray-700">
                    <CheckCircle2 size={18} className="text-red-600 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT: PROPERTY LISTING / SELL FORM */}
            <div className="lg:col-span-6">
              <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-gray-100 relative">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Post Your Property Ad
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Fill details below to get an instant market valuation & buyer reach
                    </p>
                  </div>
                  <span className="p-3 bg-red-50 text-red-600 rounded-2xl">
                    <Home size={22} />
                  </span>
                </div>

                {submitted ? (
                  <div className="py-12 text-center">
                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                      ✓
                    </div>
                    <h4 className="text-xl font-bold text-gray-900">
                      Listing Initiated!
                    </h4>
                    <p className="text-sm text-gray-600 mt-2 max-w-sm mx-auto">
                      We've forwarded your details to our WhatsApp property desk.
                      Our valuation team will connect with you within 30 minutes.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="mt-6 px-6 py-2.5 bg-red-600 text-white rounded-full text-xs font-bold uppercase tracking-wider hover:bg-red-700 transition"
                    >
                      Post Another Property
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                          Your Name *
                        </label>
                        <input
                          type="text"
                          required
                          name="ownerName"
                          placeholder="e.g. Ramesh Kumar"
                          value={formData.ownerName}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          required
                          name="phone"
                          placeholder="+91 98765 43210"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                          Property Type
                        </label>
                        <select
                          name="propertyType"
                          value={formData.propertyType}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition bg-white"
                        >
                          <option>Apartment</option>
                          <option>Independent Villa / Rowhouse</option>
                          <option>Plot / Land</option>
                          <option>Commercial Space</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                          Configuration
                        </label>
                        <select
                          name="bhk"
                          value={formData.bhk}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition bg-white"
                        >
                          <option>1 BHK</option>
                          <option>2 BHK</option>
                          <option>3 BHK</option>
                          <option>4 BHK</option>
                          <option>5+ BHK / Penthouse</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                          Project / Society Name
                        </label>
                        <input
                          type="text"
                          name="projectName"
                          placeholder="e.g. Prestige Waterford"
                          value={formData.projectName}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                          Location / Area *
                        </label>
                        <input
                          type="text"
                          required
                          name="location"
                          placeholder="e.g. Whitefield, Bengaluru"
                          value={formData.location}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                          Expected Price (₹)
                        </label>
                        <input
                          type="text"
                          name="expectedPrice"
                          placeholder="e.g. ₹1.5 Cr or negotiable"
                          value={formData.expectedPrice}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                          Super Built-Up Area
                        </label>
                        <input
                          type="text"
                          name="area"
                          placeholder="e.g. 1,650 sq.ft."
                          value={formData.area}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="w-full py-4 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-bold text-sm transition-all duration-300 shadow-lg shadow-red-500/25 hover:shadow-red-500/40 flex items-center justify-center gap-2 mt-4"
                    >
                      <Send size={16} />
                      Post Ad & Connect on WhatsApp
                    </button>

                    <p className="text-[11px] text-gray-400 text-center mt-2">
                      🔒 Your contact information is kept private. We only connect you with verified buyers.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= HOW WE SHOWCASE YOUR PROPERTY ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-14 py-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <span className="text-red-600 font-bold text-xs uppercase tracking-widest">
              Live Showcases
            </span>
            <h2 className="text-3xl font-black text-gray-900 mt-1">
              How Your Property Will Be Showcased
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              We present every listing with high-resolution photography, verified specs, and direct WhatsApp buyer actions.
            </p>
          </div>

          <a
            href="https://wa.me/message/MDG2ZSZNG6P6G1"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-bold text-red-600 hover:text-red-700"
          >
            <PhoneCall size={16} /> Have questions? Chat with our Listing Desk
          </a>
        </div>

        {/* PROPERTY CARDS (Exact same card layout as Home/Buy pages) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {sampleSoldProperties.map((property) => (
            <motion.div
              key={property.id}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col group"
            >
              {/* IMAGE CONTAINER */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={property.image}
                  alt={property.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />

                {/* GRADIENT OVERLAY */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />

                {/* TOP BADGES */}
                <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                  <span className="bg-red-600/90 backdrop-blur-md text-white text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow">
                    {property.status}
                  </span>

                  <button
                    type="button"
                    onClick={(e) => handleShare(e, property)}
                    aria-label={`Share ${property.title}`}
                    className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-md hover:bg-white text-gray-700 hover:text-black flex items-center justify-center shadow transition-all duration-200 hover:scale-105"
                  >
                    <Share2 size={16} />
                  </button>
                </div>

                {/* BOTTOM HIGHLIGHT ON IMAGE */}
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-white">
                  <div>
                    <span className="text-[11px] uppercase tracking-widest text-red-200 font-semibold">
                      {property.highlight}
                    </span>
                    <p className="text-xl font-black drop-shadow-sm">
                      {property.price}
                    </p>
                  </div>
                  <span className="text-xs font-semibold bg-black/40 backdrop-blur-md px-3 py-1 rounded-full">
                    {property.type}
                  </span>
                </div>
              </div>

              {/* CONTENT */}
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-red-600 transition-colors line-clamp-1">
                  {property.title}
                </h3>

                <div className="flex items-center gap-1.5 text-gray-500 text-xs mt-2">
                  <MapPin size={14} className="text-red-500 shrink-0" />
                  <span className="truncate">{property.location}</span>
                </div>

                <p className="text-gray-600 text-xs mt-3 line-clamp-2 leading-relaxed">
                  {property.description}
                </p>

                {/* PROPERTY HIGHLIGHTS GRID */}
                <div className="grid grid-cols-2 gap-2 mt-5 p-3 rounded-2xl bg-gray-50 border border-gray-100 text-xs text-gray-700">
                  <div className="flex items-center gap-2">
                    <Ruler size={14} className="text-red-600" />
                    <span className="font-semibold truncate">
                      {property.area}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building2 size={14} className="text-red-600" />
                    <span className="font-semibold truncate">
                      {property.units}
                    </span>
                  </div>
                </div>

                {/* ACTION BUTTONS */}
                <div className="mt-6 pt-4 border-t border-gray-100 flex items-center gap-3">
                  <Link
                    to={property.url}
                    className="flex-1 py-3 px-4 bg-gray-900 hover:bg-red-600 text-white rounded-xl font-semibold text-xs transition-colors duration-300 flex items-center justify-center gap-1.5 group/btn"
                  >
                    <span>View Showcase</span>
                    <ArrowRight
                      size={14}
                      className="group-hover/btn:translate-x-1 transition-transform"
                    />
                  </Link>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(
                        "https://wa.me/message/MDG2ZSZNG6P6G1",
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                    className="py-3 px-5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold text-xs transition-colors shadow-md hover:shadow-red-500/20"
                  >
                    Contact
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= 4-STEP SELLING PROCESS ================= */}
      <section className="bg-white py-16 px-4 sm:px-6 lg:px-14 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-red-600 font-bold text-xs uppercase tracking-widest">
              Simple 4 Steps
            </span>
            <h2 className="text-3xl font-black text-gray-900 mt-1">
              How Selling Works on HousyHunt
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="p-6 rounded-3xl bg-gray-50 border border-gray-100">
              <div className="w-12 h-12 bg-red-600 text-white font-black text-lg rounded-2xl flex items-center justify-center mb-4">
                1
              </div>
              <h4 className="font-bold text-gray-900 text-base">
                List & Valuation
              </h4>
              <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                Submit property details online. Our local pricing specialists provide
                an accurate, data-backed market valuation.
              </p>
            </div>

            <div className="p-6 rounded-3xl bg-gray-50 border border-gray-100">
              <div className="w-12 h-12 bg-red-600 text-white font-black text-lg rounded-2xl flex items-center justify-center mb-4">
                2
              </div>
              <h4 className="font-bold text-gray-900 text-base">
                Free Media Shoot
              </h4>
              <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                Our photographers visit your property for professional wide-angle
                photos, video reels, and floor mapping.
              </p>
            </div>

            <div className="p-6 rounded-3xl bg-gray-50 border border-gray-100">
              <div className="w-12 h-12 bg-red-600 text-white font-black text-lg rounded-2xl flex items-center justify-center mb-4">
                3
              </div>
              <h4 className="font-bold text-gray-900 text-base">
                Verified Buyer Visits
              </h4>
              <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                We pre-screen buyers for budget readiness and accompany all site
                visits so you don't face constant interruptions.
              </p>
            </div>

            <div className="p-6 rounded-3xl bg-gray-50 border border-gray-100">
              <div className="w-12 h-12 bg-red-600 text-white font-black text-lg rounded-2xl flex items-center justify-center mb-4">
                4
              </div>
              <h4 className="font-bold text-gray-900 text-base">
                Closing & Registry
              </h4>
              <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                Our legal team handles sale agreement drafting, token collection,
                e-Stamp duty, and sub-registrar registration.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Sell;
